import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { http } from "../../axios/axios";
import { toast } from "react-toastify";

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  accept,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
  maxLength,
}) => {
  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm md:px-4 md:py-3 md:text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base"
      >
        {label}
        {validation?.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          {...register(name, validation)}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select
          id={name}
          {...register(name, validation)}
          className={inputClass}
        >
          <option value={""}>{placeholder}</option>
          {options.map((item) => (
            <option key={item?.value} value={item?.value}>
              {item?.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          accept={accept}
          placeholder={placeholder}
          {...register(name, validation)}
          className={inputClass}
        />
      )}
      {errors[name] && (
        <p className="mt-1 text-xs text-red-500 md:text-sm ">
          {errors[name].message}
        </p>
      )}
    </div>
  );
};
const ReviewFormModal = ({ onClose, onSuccess }) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();
  const [products, setProducts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const onSubmit = async (data) => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value);
    });

    photos.forEach((photo) => {
      formData.append("photos", photo.file);
    });

    try {
      await http.post("reviews", formData);

      photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
      reset();
      setPhotos([]);
      onSuccess();
      onClose();

      toast.success("Review submitted! Waiting for admin approval.");
    } catch (err) {
      console.error(err.response || err);
      toast.error("Failed to submit review.");
    }
  };
  const fetchProduct = async () => {
    try {
      const res = await http.get("/products/productName");
      setProducts(res.data?.data);
    } catch (err) {
      console.error(err.response || err);
    }
  };
  useEffect(() => {
    fetchProduct();
  }, []);

  const MAX_IMAGES = 2;
  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > MAX_IMAGES) {
      toast.error(`You an upload a maximum of ${MAX_IMAGES} images`);
      e.target.value = "";
      return;
    }
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
    e.target.value = "";
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/90 backdrop:-blur-xl z-[9999]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[92%] md:max-w-4xl max-h-[80vh] overflow-y-auto rounded-md bg-white
        "
      >
        <div className=" flex items-center justify-between px-4 py-8 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Share Your Review
            </h2>
            <p className="text-sm text-gray-600 mt-4">
              We'd love to hear about your experience with our products and
              services. Your feedback helps us improve and assists other
              customers in making informed decisions.
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-red-500 hover:text-white transition flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-8">
            <InputField
              label="Full Name"
              name="name"
              placeholder="Enter your full name"
              register={register}
              errors={errors}
              validation={{
                required: "Full name is required",
              }}
            />

            <InputField
              label="Email Address"
              name="email"
              type="email"
              placeholder="Enter your email"
              register={register}
              errors={errors}
              validation={{
                required: "Email is required",
              }}
            />
            <InputField
              label={"Product"}
              name={"productId"}
              as="select"
              placeholder={"Select a product"}
              register={register}
              options={products.map((product) => ({
                label: product?.title,
                value: product?._id,
              }))}
              errors={errors}
              validation={{
                required: "Please select product",
              }}
            />

            <InputField
              label={"Rating"}
              name={"rating"}
              as="select"
              placeholder={"Select rating"}
              register={register}
              errors={errors}
              validation={{
                required: "Please provide a rating",
              }}
              options={[
                { label: "⭐⭐⭐⭐⭐ (5)", value: "5" },
                { label: "⭐⭐⭐⭐ (4)", value: "4" },
                { label: "⭐⭐⭐ (3)", value: "3" },
                { label: "⭐⭐ (2)", value: "2" },
                { label: "⭐ (1)", value: "1" },
              ]}
            />

            <InputField
              label={"Service Used"}
              name={"serviceUsed"}
              placeholder={
                "e.g. CCTV Installation, Access Control, Biometric System"
              }
              register={register}
              errors={errors}
              validation={{
                required: "Please specify the service used",
              }}
            />
            <div className="md:col-span-2">
              <InputField
                label={"Review"}
                name={"review"}
                as="textarea"
                rows={5}
                maxLength={620}
                placeholder="Tell us about your experience... (260–620 characters)"
                register={register}
                errors={errors}
                validation={{
                  required: "Review is required",
                  minLength: {
                    value: 260,
                    message:
                      "Please write at least 260 characters to properly describe your experience.",
                  },
                  maxLength: {
                    value: 620,
                    message: "Please keep your review under 620 characters.",
                  },
                }}
              />
            </div>
            <div className="md:col-span-2 py-4">
              <div>
                <label className="mb-1.5 block text-s font-semibold text-slate-700 md:mb-2 md:text-base">
                  Upload Photos (Up to 2)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={photos?.length >= MAX_IMAGES}
                  onChange={handlePhotoChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
                />
                {photos.length > 0 && (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-4">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative">
                        <img
                          src={photo?.preview}
                          alt={`Preview ${index + 1}`}
                          className="h-32 w-full rounded-lg border object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-6 py-3 font-medium hover:bg-gray-100 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700 transition"
            >
              Submit Review
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
export default ReviewFormModal;
